import * as React from 'react';

import type {
    EditionUpdateInput,
    EditionFragment as Edition,
    SmsEditionFragment as SmsEdition,
    SmsEditionUpdateInput,
} from 'nutshell-graphql-types';

import {useSyncMcfxAudience} from './send-to-audience/graphql/hooks/use-sync-mcfx-audience';
import {mapAudienceToSelectItem} from './send-to-audience/map-audience-to-select-item';
import type {AudienceOption} from './send-to-audience/map-audience-to-select-item';
import {SendToAudienceField} from './send-to-audience/send-to-audience-field';
import {SendToSavedListField} from './send-to-saved-list-field';
import {ToFieldNewAudienceManagement} from './to-field-new-audience-management';

type Props = {
    edition: Edition | SmsEdition;
    isTemplate: boolean;
    showPreviewMode: boolean;
    shouldShowNewAudienceManagement: boolean;
    setIsPreparingToSend: (arg1: boolean) => void;
    onEditionSave: (saveData: EditionUpdateInput | SmsEditionUpdateInput) => Promise<any>;
    onSetSelectedEditorTab: (arg1: string) => void;
};

export function ToField(props: Props) {
    const {syncMcfxAudience} = useSyncMcfxAudience();

    const selectedAudience: AudienceOption | null | undefined = props.edition.audience
        ? mapAudienceToSelectItem(props.edition.audience)
        : null;
    // @ts-expect-error
    const handleAudienceSelection = (newSelectedAudience) => {
        props.onEditionSave({
            editionId: props.edition.id,
            audienceId:
                newSelectedAudience && !newSelectedAudience.requiresSync
                    ? newSelectedAudience.value
                    : undefined,
        });
    };

    let sendToComponent = null;
    if (props.edition.audience && props.edition.audience.fromSavedFilter) {
        sendToComponent = (
            <SendToSavedListField savedFilter={props.edition.audience.fromSavedFilter} />
        );
    } else if (!props.isTemplate) {
        sendToComponent = (
            <SendToAudienceField
                isDisabled={props.showPreviewMode}
                selectedAudience={selectedAudience}
                onSyncMcfxAudience={(audience) => {
                    const syncingSelectedAudience = {
                        ...selectedAudience,
                        isSyncing: true,
                    } as const;
                    handleAudienceSelection(syncingSelectedAudience);

                    syncMcfxAudience({
                        name: audience.label,
                        mcfxAudienceId: audience.value,
                    })
                        .then((emAudience) => {
                            handleAudienceSelection(mapAudienceToSelectItem(emAudience));
                        })
                        .catch(() => {
                            const nonSyncingSelectedAudience = {
                                ...selectedAudience,
                                isSyncing: false,
                            } as const;
                            handleAudienceSelection(nonSyncingSelectedAudience);
                        });
                }}
                onChange={handleAudienceSelection}
                isSmsEdition={
                    props.edition.__typename === 'SmsEdition' && NutClientConfig.hasSmsAudiencesFF
                }
            />
        );
    }

    return (
        <>
            {props.shouldShowNewAudienceManagement ? (
                <ToFieldNewAudienceManagement
                    setIsPreparingToSend={() => {
                        props.setIsPreparingToSend(true);
                        props.onSetSelectedEditorTab('prepare');
                    }}
                    canSend={!props.edition.deletedTime}
                />
            ) : (
                <>{sendToComponent}</>
            )}
        </>
    );
}
